import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import axiosInstance from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "@/constants/config";
import toast from "react-hot-toast";

function getStudentName(student) {
  return (
    student.name ||
    [student.firstName, student.middleName, student.lastName].filter(Boolean).join(" ") ||
    student.username ||
    "Unnamed Student"
  );
}

export default function VehicleAttendance() {
  const [vehicle, setVehicle] = useState(null);
  const [schoolId, setSchoolId] = useState(null);
  const [route, setRoute] = useState(null);
  const [selectedShiftId, setSelectedShiftId] = useState(null);
  const [attendanceType, setAttendanceType] = useState("pickup");
  const [attendance, setAttendance] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Load vehicle and schoolId from localStorage
  useEffect(() => {
    try {
      const v = JSON.parse(localStorage.getItem("vehicle"));
      setVehicle(v);
      setSchoolId(v?.school?._id || v?.school);
    } catch {
      setVehicle(null);
      setSchoolId(null);
    }
  }, []);

  // Fetch vehicle route from backend
  useEffect(() => {
    if (!schoolId || !vehicle) return;
    setLoading(true);
    setError("");
    axiosInstance
      .get(API_ENDPOINTS.vehicleRoutesBySchool(schoolId))
      .then((res) => {
        // Find the route for this vehicle
        const allRoutes = res.data?.data?.vehicleRoutes || [];
        const myRoute = allRoutes.find(
          (r) => r.vehicle?.vehicleNumber === vehicle.vehicleNumber
        );
        setRoute(myRoute || null);
        setLoading(false);
      })
      .catch((err) => {
        setError(
          err.response?.data?.message || "Failed to fetch vehicle route."
        );
        setLoading(false);
      });
  }, [schoolId, vehicle]);

  // Reset attendance when shift changes
  useEffect(() => {
    setAttendance({});
    setSubmitted(false);
  }, [selectedShiftId, attendanceType]);

  // Handle attendance checkbox change
  const handleAttendanceChange = (stopId, studentId, checked) => {
    setAttendance((prev) => ({
      ...prev,
      [stopId]: {
        ...prev[stopId],
        [studentId]: checked,
      },
    }));
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!route || !selectedShiftId) return;
    setError("");
    setLoading(true);
    try {
      const shift = route.shifts.find((s) => s._id === selectedShiftId);
      const stops = shift?.stops || [];
      const vehicleId = vehicle.id || vehicle._id;
      for (const stop of stops) {
        const students = Object.entries(attendance[stop._id] || {}).map(
          ([studentId, present]) => ({ student: studentId, present })
        );
        if (students.length === 0) continue; // Skip stops with no students
        
        await axiosInstance.post(API_ENDPOINTS.attendanceBusStop, {
          vehicle: vehicleId,
          vehicleRoute: route._id,
          shift: selectedShiftId,
          stop: stop._id,
          date: new Date().toISOString().slice(0, 10),
          students,
          type: attendanceType, 
        });
      }
      setSubmitted(true);
      toast.success("Attendance submitted for all stops!");
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to submit attendance."
      );
      toast.error(err.response?.data?.message || "Failed to submit attendance.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto mt-8 text-center text-gray-600">Loading...</div>
    );
  }
  if (error) {
    return (
      <div className="max-w-2xl mx-auto mt-8 text-center text-red-600">{error}</div>
    );
  }
  if (!route) {
    return (
      <div className="max-w-2xl mx-auto mt-8 text-center text-gray-600">No route assigned to this vehicle.</div>
    );
  }

  // Get shifts for the selected attendance type
  const shifts = route.shifts.filter((s) => s.type === attendanceType);
  const selectedShift = shifts.find((s) => s._id === selectedShiftId);

  return (
    <div className="max-w-2xl mx-auto mt-8">
      <Card>
        <CardHeader>
          <CardTitle>Vehicle Attendance</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Step 1: Select Shift */}
          {!selectedShiftId && (
            <div>
              <h2 className="text-lg font-semibold mb-4">Select Shift</h2>
              <Tabs value={attendanceType} onValueChange={setAttendanceType} className="w-full mb-4">
                <TabsList>
                  <TabsTrigger value="pickup">Pickup</TabsTrigger>
                  <TabsTrigger value="drop">Drop</TabsTrigger>
                </TabsList>
              </Tabs>
              <div className="space-y-2">
                {shifts.map((shift) => (
                  <Button key={shift._id} className="w-full" onClick={() => setSelectedShiftId(shift._id)}>
                    {shift.name} ({shift.startTime} - {shift.endTime})
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Mark Attendance for Stops with Tabs for Pickup/Drop */}
          {selectedShiftId && !submitted && selectedShift && (
            <div>
              <Button variant="ghost" onClick={() => setSelectedShiftId(null)} className="mb-4">Change Shift</Button>
              {selectedShift.stops.map((stop) => (
                <div key={stop._id} className="mb-6 border rounded p-4">
                  <h3 className="font-semibold mb-2">{stop.name}</h3>
                  <div className="space-y-2">
                    {stop.students.map((student) => (
                      <div key={student._id} className="flex items-center gap-2">
                        <Checkbox
                          id={`${stop._id}-${student._id}`}
                          checked={attendance[stop._id]?.[student._id] || false}
                          onCheckedChange={(checked) =>
                            handleAttendanceChange(stop._id, student._id, checked)
                          }
                        />
                        <label htmlFor={`${stop._id}-${student._id}`}>{getStudentName(student)}</label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <Button className="w-full mt-4" onClick={handleSubmit} disabled={loading}>
                {loading ? "Submitting..." : "Submit Attendance"}
              </Button>
            </div>
          )}

          {/* Step 3: Final Submit/Review */}
          {submitted && (
            <div>
              <h2 className="text-lg font-semibold mb-4">Attendance Submitted!</h2>
              <div className="mb-4">
                <strong>Shift:</strong> {selectedShift?.name}
                <br />
                <strong>Type:</strong> {attendanceType.charAt(0).toUpperCase() + attendanceType.slice(1)}
              </div>
              <div>
                {selectedShift?.stops.map((stop) => (
                  <div key={stop._id} className="mb-4">
                    <h3 className="font-semibold">{stop.name}</h3>
                    <ul className="list-disc ml-6">
                      {stop.students.map((student) => (
                        <li key={student._id}>
                          {getStudentName(student)}: {attendance[stop._id]?.[student._id] ? "Present" : "Absent"}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <Button className="mt-4" onClick={() => setSubmitted(false)}>
                Edit Attendance
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 